import { createFileRoute } from '@tanstack/react-router'
import React, { useState } from 'react'
import { useQuery as useConvexQuery } from 'convex/react'
import { api } from 'convex/_generated/api'
import { usePreviewUser } from '@/hooks/usePreviewUser'
import { SignInButton, SignUpButton, useAuth } from '@clerk/clerk-react'
import type { Id } from 'convex/_generated/dataModel'

export const Route = createFileRoute('/preview/lyrics')({
  component: PreviewLyricsPage,
})

function PreviewLyricsPage() {
  const previewUser = usePreviewUser()

  // Show loading while checking preview user
  if (previewUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  return <PreviewLyricsPageContent />
}

function PreviewLyricsPageContent() {
  const { isSignedIn } = useAuth()
  const [prompt, setPrompt] = useState('')
  const [showSignInModal, setShowSignInModal] = useState(false)

  // Query preview user's lyrics generations
  const lyricsGenerations = useConvexQuery(api.preview.getPreviewLyrics, {})

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // If user is not signed in, show sign-in modal
    if (!isSignedIn) {
      setShowSignInModal(true)
      return
    }

    // If signed in, redirect to app/lyrics (this shouldn't happen in preview mode)
    // But handle it just in case
  }

  return (
    <div className="min-h-screen bg-black text-white py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Preview Banner */}
        <div className="mb-4 bg-terminal-green/20 border-2 border-terminal-green p-3 text-center">
          <p className="text-terminal-green font-mono text-sm font-bold">
            [PREVIEW MODE] • Sign up to create and save your lyrics
          </p>
        </div>

        {/* Header */}
        <div className="mb-8 terminal-border p-6">
          <h1 className="text-4xl font-bold text-sky-blue mb-2 font-mono tracking-wider">
            [LYRICS GENERATOR]
          </h1>
          <p className="text-trail-tan font-mono mb-2">
            Generate song lyrics with AI - describe the theme, mood, or story you want
          </p>
          <div className="bg-yellow-900 border-2 border-yellow-500 rounded-lg p-3 mt-4">
            <p className="text-yellow-200 font-mono text-sm">
              <span className="font-bold">⚠️ NOTE:</span> This endpoint generates AI lyrics from your prompt.
              <br />
              To create a SONG with your own lyrics, visit the <span className="text-sky-blue">Songs</span> page instead.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <div className="terminal-border p-6 bg-black">
              <h2 className="text-2xl font-bold text-terminal-green mb-4 font-mono">
                ═══ CREATE NEW LYRICS ═══
              </h2>

              {/* Sign In Modal */}
              {showSignInModal && (
                <div className="bg-black border-2 border-terminal-green p-4 mb-6">
                  <p className="text-terminal-green font-mono font-bold text-sm mb-4">
                    Sign in to generate lyrics
                  </p>
                  <div className="flex gap-3">
                    <SignUpButton mode="modal">
                      <button
                        type="button"
                        className="px-6 py-3 bg-terminal-green text-black font-bold font-mono text-sm border-2 border-terminal-green hover:bg-black hover:text-terminal-green transition-all"
                      >
                        [SIGN UP]
                      </button>
                    </SignUpButton>
                    <SignInButton mode="modal">
                      <button
                        type="button"
                        className="px-6 py-3 bg-black text-trail-tan font-bold font-mono text-sm border-2 border-trail-tan hover:bg-trail-tan hover:text-black transition-all"
                      >
                        [SIGN IN]
                      </button>
                    </SignInButton>
                    <button
                      type="button"
                      onClick={() => setShowSignInModal(false)}
                      className="px-6 py-3 bg-black text-gray-400 font-bold font-mono text-sm border-2 border-gray-600 hover:bg-gray-900 transition-all"
                    >
                      [CANCEL]
                    </button>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                    ► PROMPT / THEME
                  </label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe the lyrics you want to generate...
Example: 'A melancholic song about learning a new language and the journey of discovery'
Example: 'Upbeat lyrics about traveling and adventure in Spanish'
Example: 'Romantic French song about Paris in springtime'"
                    className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono focus:border-terminal-green focus:outline-none resize-none retro-glow transition-all"
                    rows={8}
                    required
                  />
                  <p className="text-trail-tan text-xs font-mono mt-2">
                    💡 TIP: Include language, mood, theme, and story details for best results
                  </p>
                </div>

                <div className="bg-black border-2 border-trail-tan p-4">
                  <p className="text-trail-tan font-mono text-sm mb-2">
                    <span className="text-sky-blue">►</span> What you&apos;ll get:
                  </p>
                  <ul className="space-y-1 text-trail-tan font-mono text-xs">
                    <li><span className="text-terminal-green">•</span> AI-generated song lyrics</li>
                    <li><span className="text-terminal-green">•</span> Structured verses and choruses</li>
                    <li><span className="text-terminal-green">•</span> Ready to use with instrumental generator</li>
                    <li><span className="text-terminal-green">•</span> Extract vocabulary for learning</li>
                  </ul>
                </div>

                <button
                  type="submit"
                  disabled={!prompt.trim()}
                  className="w-full px-8 py-4 bg-terminal-green text-black font-bold font-mono text-lg border-4 border-terminal-green hover:bg-black hover:text-terminal-green disabled:bg-gray-700 disabled:border-gray-700 disabled:text-gray-500 transition-all retro-glow tracking-wider"
                >
                  ► GENERATE LYRICS ◄
                </button>
              </form>
            </div>
          </div>

          {/* Recent Generations Sidebar */}
          <div className="lg:col-span-1">
            <div className="terminal-border p-6 bg-black sticky top-8">
              <h2 className="text-xl font-bold text-sky-blue mb-4 font-mono">
                [RECENT LYRICS]
              </h2>
              {lyricsGenerations === undefined ? (
                <p className="text-trail-tan font-mono text-sm">Loading...</p>
              ) : lyricsGenerations.length === 0 ? (
                <p className="text-trail-tan font-mono text-sm">
                  No lyrics generated yet. Sign in to create your first one!
                </p>
              ) : (
                <div className="space-y-3">
                  {lyricsGenerations.map((gen) => (
                    <LyricsCard key={gen._id} generation={gen} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function LyricsCard({
  generation,
}: {
  generation: {
    _id: Id<'temporGenerations'>
    type: string
    status: string
    prompt?: string
    lyrics?: string
    _creationTime: number
  }
}) {
  const [isExpanded, setIsExpanded] = useState(false)

  const statusColors = {
    pending: 'bg-yellow-900 text-yellow-300 border-yellow-500',
    processing: 'bg-blue-900 text-blue-300 border-blue-500',
    completed: 'bg-green-900 text-green-300 border-green-500',
    failed: 'bg-red-900 text-red-300 border-red-500',
  }

  const statusColor = statusColors[generation.status as keyof typeof statusColors] || 'bg-gray-900 text-gray-300 border-gray-500'

  return (
    <div className="p-4 bg-black border-2 border-sky-blue rounded-lg hover:border-terminal-green transition-all">
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs font-mono font-bold text-trail-tan">LYRICS</span>
        <span className={`px-2 py-0.5 rounded border-2 text-xs font-mono font-bold ${statusColor}`}>
          {generation.status.toUpperCase()}
        </span>
      </div>

      {generation.prompt && (
        <p className="text-xs text-trail-tan mb-2 line-clamp-2 font-mono">
          {generation.prompt}
        </p>
      )}

      {generation.lyrics && generation.status === 'completed' && (
        <div className="mt-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs text-sky-blue hover:text-terminal-green font-mono font-bold transition-all"
            type="button"
          >
            {isExpanded ? '▼ Hide Lyrics' : '► View Lyrics'}
          </button>
          {isExpanded && (
            <div className="mt-2 p-3 bg-black border-2 border-trail-tan max-h-64 overflow-y-auto">
              <pre className="text-xs text-white font-mono whitespace-pre-wrap">
                {generation.lyrics}
              </pre>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-gray-500 mt-2 font-mono">
        {new Date(generation._creationTime).toLocaleDateString()} {new Date(generation._creationTime).toLocaleTimeString()}
      </p>
    </div>
  )
}

